/**
 * Notifications Page
 * Lists the current user's notifications and marks them read on view.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Heart, MessageCircle, UserPlus, AtSign, Repeat2, Trophy, BookOpen } from 'lucide-react';
import { capture } from '../utils/analytics';
import { formatPostTimestampToRelative } from '../utils/formatPostTimestampToRelative';
import './NotificationsPage.css';

const ICONS = {
  like: <Heart size={18} />,
  comment: <MessageCircle size={18} />,
  follow: <UserPlus size={18} />,
  mention: <AtSign size={18} />,
  repost: <Repeat2 size={18} />,
  contest_win: <Trophy size={18} />,
  read_pick: <BookOpen size={18} />
};

const TEXT = {
  like: 'liked your post',
  comment: 'commented on your post',
  follow: 'started following you',
  mention: 'mentioned you in a post',
  repost: 'reposted your post',
  contest_win: 'crowned your post as this week’s contest winner',
  read_pick: 'picked your piece for Read. Tap to add notes on your best lines'
};

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get('/api/notifications');
        setNotifications(res.data.notifications || []);
        // Mark read once we've shown them.
        await axios.post('/api/notifications/read');
      } catch (error) {
        console.error('Error loading notifications:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return <div className="container loading">Loading notifications...</div>;
  }

  return (
    <div className="container">
      <h1>Notifications</h1>
      {notifications.length === 0 ? (
        <div className="no-posts"><p>No notifications yet.</p></div>
      ) : (
        <div className="notifications-list">
          {notifications.map((n) => {
            const actor = n.actor || {};
            const target = n.type === 'follow' ? `/profile/${actor._id}`
              : !n.post ? '#'
              : n.type === 'read_pick' ? `/read/${n.post._id}` : `/post/${n.post._id}`;
            return (
              <Link
                to={target}
                key={n._id}
                className={`notification-row ${n.read ? '' : 'unread'}`}
                onClick={() => capture('notification_clicked', { type: n.type })}
              >
                <span className={`notification-icon icon-${n.type}`}>{ICONS[n.type]}</span>
                {actor.avatar ? (
                  <img src={actor.avatar} alt={actor.username} className="notification-avatar" />
                ) : (
                  <div className="notification-avatar notification-avatar-placeholder">
                    {(actor.username || 'W').charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="notification-body">
                  <span>
                    <strong>{actor.fullName || actor.username || (n.actor ? 'Someone' : 'WriteFlow')}</strong> {TEXT[n.type]}
                  </span>
                  {n.post?.content && (
                    <span className="notification-snippet">"{n.post.content.slice(0, 80)}"</span>
                  )}
                  <span className="notification-time">
                    {formatPostTimestampToRelative(n.createdAt)}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
